import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  afterNextRender,
  computed,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlContainer } from '@angular/forms';

/**
 * Campo de formulario: etiqueta, control y mensaje de validación.
 *
 * Reemplaza el trío `.form-field-validated` / `.form-field` / cadena de `@if`
 * que se repetía por campo en el formulario de empleado. Antes, cada campo
 * validado costaba ~20 líneas y nombraba su control hasta cuatro veces —una en
 * `formControlName`, otra en `[class.input-error]` y dos por cada mensaje—; un
 * typo en cualquiera de esas no falla en compilación, revienta en runtime.
 * Aquí el nombre se escribe una vez, en `control`.
 *
 * ```html
 * <app-form-field label="Primer Apellido" control="primerApellido" [required]="true"
 *                 [errores]="{ required: 'El primer apellido es requerido' }"
 *                 [submitted]="submitted()">
 *   <input nz-input formControlName="primerApellido" placeholder="Ej: García" />
 * </app-form-field>
 * ```
 *
 * ### Estructura
 *
 * El host es la rejilla (`etiqueta | control`) y el control va proyectado como
 * **hijo directo**, sin envoltorio. Esto es deliberado: el contenido proyectado
 * conserva el `_ngcontent` del componente que lo declara —la página—, así que
 * un selector de este CSS nunca podría darle ancho. Siendo item de la rejilla
 * del host, lo dimensiona la columna y no hace falta seleccionarlo.
 *
 * El mensaje de error cae en la segunda columna por rejilla, lo que retira el
 * `padding-left: 152px` con el que antes se alineaba a mano (140 de etiqueta +
 * 12 de gap, y a reajustar si cambiaba cualquiera de los dos).
 *
 * ### Accesibilidad
 *
 * Ningún campo del formulario tenía la etiqueta asociada a su control: 26
 * inputs y 15 selects sin `for` ni `id`, así que el click en la etiqueta no
 * enfocaba y el lector de pantalla no anunciaba nada. El `id` se asigna solo,
 * sobre el primer elemento etiquetable del contenido proyectado, y sirve igual
 * para un `<input>` nativo que para el input interno de `nz-select`,
 * `nz-date-picker` o `nz-input-number`. Se hace en `afterNextRender` porque en
 * ese punto los componentes de ng-zorro ya montaron su template interno.
 */
@Component({
  selector: 'app-form-field',
  template: `
    <label class="form-label" [class.required]="required()" [attr.for]="campoId()">
      {{ label() }}:
    </label>

    <ng-content />

    @if (mensajeError; as msg) {
      <span class="field-error">{{ msg }}</span>
    } @else if (hint()) {
      <span class="field-hint">{{ hint() }}</span>
    }

    <ng-content select="[slot=ayuda]" />
  `,
  styleUrl: './form-field.css',
  host: {
    // La página pinta el borde rojo del control desde esta clase, con una regla
    // global: `.ant-select-selector` vive dentro del template de ng-zorro y no
    // lo alcanza ningún CSS encapsulado.
    '[class.invalido]': 'mostrarError',
    '[class.form-field--sm]': "size() === 'sm'",
    '[class.form-field--xs]': "size() === 'xs'",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormField implements OnInit {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  /** El grupo en el que vive el control: `[formGroup]` o `formGroupName`. */
  private readonly container = inject(ControlContainer, { optional: true });

  readonly label = input.required<string>();
  /**
   * Nombre del control dentro del grupo actual. Sin esto el campo se pinta
   * igual, pero sin validación —el caso de los ~30 campos sin validadores—.
   */
  readonly control = input('');
  readonly required = input(false);
  /**
   * Mensaje por clave de error (`{ required: 'El nombre es requerido' }`). Se
   * pasan explícitos y no se generan del label: en español el mensaje concuerda
   * en género con el campo ("la dirección es requerida"), y varios necesitan
   * decir algo que el label no sabe —"Este ID ya está en uso por otro
   * empleado"—.
   */
  readonly errores = input<Record<string, string>>({});
  /** Texto neutro bajo el campo. Se oculta si hay error que mostrar. */
  readonly hint = input('');
  /**
   * Con el formulario ya enviado, los errores se muestran aunque el campo nunca
   * se haya tocado: es la única forma de señalar lo que quedó vacío.
   */
  readonly submitted = input(false);
  /** Ancho de la etiqueta: 140px, 70px en medios campos, 20px en Ext/Int. */
  readonly size = input<'md' | 'sm' | 'xs'>('md');

  /**
   * Estado de validez, como getters y NO como `computed()`.
   *
   * Cuesta explicarlo pero se paga caro olvidarlo: `invalid`, `touched` y
   * `errors` de los Reactive Forms son propiedades planas, no signals. Un
   * `computed()` solo se recalcula cuando cambia alguno de los signals que
   * leyó, así que al no leer ninguno que se mueva se quedaba congelado en el
   * primer valor —false— y NINGÚN campo mostraba su error: ni al escribir algo
   * inválido, ni al salir del campo. Los getters se evalúan en cada ciclo de
   * detección, que es como funcionaban las expresiones que había en la
   * plantilla antes de este componente.
   */
  private get ctrl(): AbstractControl | null {
    const nombre = this.control();
    if (!nombre) return null;
    return this.container?.control?.get(nombre) ?? null;
  }

  get mostrarError(): boolean {
    const c = this.ctrl;
    if (!c) return false;
    return c.invalid && (c.touched || this.submitted());
  }

  get mensajeError(): string {
    if (!this.mostrarError) return '';
    const errores = this.ctrl?.errors;
    if (!errores) return '';
    const mapa = this.errores();
    // Primer error declarado que tenga mensaje: el orden del mapa es el orden de
    // prioridad, que lo decide quien lo escribe.
    for (const clave of Object.keys(mapa)) {
      if (errores[clave]) return mapa[clave];
    }
    return '';
  }

  readonly campoId = computed(() => (this.control() ? `f-${this.control()}` : null));

  /**
   * Los Reactive Forms no notifican a la detección de cambios: sus estados son
   * propiedades planas. Con `OnPush` eso deja al componente sin refrescar nunca
   * —sus inputs son constantes, y Angular memoiza el literal `[errores]` en una
   * pure function, así que ni siquiera ese cambia de referencia—, y el mensaje
   * de error no aparecía aunque el getter ya devolviera el texto correcto.
   *
   * El síntoma era desconcertante: el borde rojo SÍ se pintaba. Las host
   * bindings de un componente se evalúan durante el refresco de su padre,
   * mientras que su plantilla solo corre si el componente está marcado sucio.
   * De ahí que la clase se aplicara y el texto no.
   *
   * `control.events` cubre validez, valor y `touched` —este último no lo emite
   * `statusChanges`—, que son los tres estados de los que depende el mensaje.
   */
  ngOnInit(): void {
    this.ctrl?.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  constructor() {
    afterNextRender(() => {
      const id = this.campoId();
      if (!id) return;
      const control = this.host.nativeElement.querySelector<HTMLElement>(
        'input, textarea, select, button',
      );
      if (control && !control.id) control.id = id;
    });
  }
}
